import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DollarSign, Clock, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, Users, TrendingUp, Wallet, Undo2, ShieldAlert, History, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';
import IsoOutstanding from '../components/syndication/IsoOutstanding';  // 11/03 Josh: what I still owe ISOs

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── System Breakdown Dashboard (Admin) ────────────────────────────────── */

const SystemBreakdown = ({ getAuthHeaders }) => {
  const isAdmin = true; // SystemBreakdown is rendered only for admin (gated by parent)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [activeTab, setActiveTab] = useState({}); // { [userId]: 'broker' | 'history' }
  const [selectedDeals, setSelectedDeals] = useState({});
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payUserId, setPayUserId] = useState(null);
  const [payUserName, setPayUserName] = useState('');
  const [payForm, setPayForm] = useState({ payment_method: 'wire', reference_number: '', payment_date: '', notes: '' });
  const [paying, setPaying] = useState(false);
  // Payment history per user
  const [userPayments, setUserPayments] = useState({});
  const [loadingPayments, setLoadingPayments] = useState({});
  // Clawback state
  const [clawbackDialogOpen, setClawbackDialogOpen] = useState(false);
  const [clawbackDeal, setClawbackDeal] = useState(null);
  const [clawbackUserId, setClawbackUserId] = useState(null);
  const [clawingBack, setClawingBack] = useState(false);
  // Reverse-clawback state (Phase 2A)
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reverseDeal, setReverseDeal] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reversing, setReversing] = useState(false);
  // Clawback reason (Phase 2B Part 6)
  const [clawbackReason, setClawbackReason] = useState('');
  // Undo state
  const [undoing, setUndoing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commissions/system-breakdown`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch {
      toast.error('Failed to load commission breakdown');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchPaymentHistory = async (userId) => {
    setLoadingPayments(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await axios.get(`${API}/commissions/payments?user_id=${userId}`, { headers: getAuthHeaders() });
      setUserPayments(prev => ({ ...prev, [userId]: res.data }));
    } catch {
      toast.error('Failed to load payment history');
    } finally {
      setLoadingPayments(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-xl" />;
  if (!data) return null;

  const { summary, users } = data;

  const getActiveTab = (uid) => activeTab[uid] || 'broker';

  const toggleUser = (uid) => {
    if (expandedUser === uid) {
      setExpandedUser(null);
    } else {
      setExpandedUser(uid);
      setSelectedDeals({});
      if (!activeTab[uid]) setActiveTab(prev => ({ ...prev, [uid]: 'broker' }));
    }
  };

  const switchTab = (uid, tab) => {
    setActiveTab(prev => ({ ...prev, [uid]: tab }));
    if (tab === 'history' && !userPayments[uid]) {
      fetchPaymentHistory(uid);
    }
  };

  const toggleDealSelection = (dealId, amount) => {
    setSelectedDeals(prev => {
      const next = { ...prev };
      if (next[dealId]) { delete next[dealId]; } else { next[dealId] = { deal_id: String(dealId), amount }; }
      return next;
    });
  };

  const selectedTotal = Object.values(selectedDeals).reduce((s, d) => s + d.amount, 0);
  const selectedCount = Object.keys(selectedDeals).length;

  const openPayDialog = (userId, userName) => {
    setPayUserId(userId);
    setPayUserName(userName);
    setPayForm({ payment_method: 'wire', reference_number: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' });
    setPayDialogOpen(true);
  };

  const handlePay = async () => {
    if (selectedCount === 0) return;
    setPaying(true);
    try {
      await axios.post(`${API}/commissions/pay`, {
        user_id: payUserId,
        deal_payments: Object.values(selectedDeals),
        ...payForm,
      }, { headers: getAuthHeaders() });
      toast.success(`Payment of ${formatCurrency(selectedTotal)} recorded for ${payUserName}`);
      setPayDialogOpen(false);
      setSelectedDeals({});
      setLoading(true);
      await fetchData();
      // Refresh history if it was loaded
      if (userPayments[payUserId]) fetchPaymentHistory(payUserId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const handleUndoPayment = async (paymentId, userId) => {
    setUndoing(paymentId);
    try {
      const res = await axios.post(`${API}/commissions/undo-payment`, { payment_id: paymentId }, { headers: getAuthHeaders() });
      toast.success(res.data.message || 'Payment reversed');
      // Refresh data
      setLoading(true);
      await fetchData();
      fetchPaymentHistory(userId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to undo payment');
    } finally {
      setUndoing(null);
    }
  };

  const openClawbackDialog = (deal, userId) => {
    setClawbackDeal(deal);
    setClawbackUserId(userId);
    setClawbackReason('');
    setClawbackDialogOpen(true);
  };

  const handleClawback = async () => {
    if (!clawbackDeal) return;
    setClawingBack(true);
    try {
      const res = await axios.post(`${API}/commissions/clawback`, {
        deal_uuid: clawbackDeal.deal_uuid,
        user_id: clawbackUserId,
        reason: clawbackReason.trim() || null,
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message || 'Commission clawed back');
      setClawbackDialogOpen(false);
      setClawbackDeal(null);
      setClawbackReason('');
      setLoading(true);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clawback failed');
    } finally {
      setClawingBack(false);
    }
  };

  const openReverseClawbackDialog = (deal) => {
    setReverseDeal(deal);
    setReverseReason('');
    setReverseDialogOpen(true);
  };

  const handleReverseClawback = async () => {
    if (!reverseDeal) return;
    if (!reverseReason.trim()) {
      toast.error('Please provide a reason for reversing the clawback');
      return;
    }
    setReversing(true);
    try {
      const res = await axios.post(`${API}/commissions/clawback/reverse`, {
        deal_uuid: reverseDeal.deal_uuid,
        reason: reverseReason.trim(),
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message || 'Clawback reversed');
      setReverseDialogOpen(false);
      setReverseDeal(null);
      setReverseReason('');
      setLoading(true);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reverse clawback');
    } finally {
      setReversing(false);
    }
  };

  const roleBadge = (role) => {
    const colors = {
      broker: 'bg-blue-100 text-blue-700',
      iso: 'bg-violet-100 text-violet-700',
      admin: 'bg-gray-200 text-gray-600',
    };
    return <Badge key={role} className={`${colors[role] || 'bg-gray-100'} text-[10px] mr-1`}>{role}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="system-breakdown">
      {/* Summary Cards — no syndication */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50"><DollarSign className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-gray-500">Total Earned</p>
                <p className="text-xl font-bold" data-testid="total-earned">{formatCurrency(summary.grand_total_earned)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-50"><Wallet className="h-5 w-5 text-red-600" /></div>
              <div>
                <p className="text-xs text-gray-500">Total Owed</p>
                <p className="text-xl font-bold text-red-600" data-testid="total-owed">{formatCurrency(summary.grand_total_owed)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50"><CheckCircle className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-xs text-gray-500">Total Paid</p>
                <p className="text-xl font-bold text-green-600" data-testid="total-paid">{formatCurrency(summary.grand_total_paid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-User Breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg"><Users className="h-5 w-5" /> Commission Breakdown by User</CardTitle>
            <span className="text-sm text-gray-500">{summary.total_users} user{summary.total_users !== 1 ? 's' : ''}</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50/80">
                <TableHead className="w-8"></TableHead>
                <TableHead>User</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead className="text-right">Deals</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Owed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    No commission data yet. Breakdowns appear here once deals have a broker or ISO assigned and commissions are recorded.
                  </TableCell>
                </TableRow>
              ) : users.map(u => {
                const isExpanded = expandedUser === u.user_id;
                // Allow expanding if the user has ANY deals (including clawed-back),
                // even though broker_deals_count only counts active ones.
                const hasDetail = (u.broker_deals?.length || 0) > 0;
                const tab = getActiveTab(u.user_id);
                const payments = userPayments[u.user_id] || [];
                return (
                  <React.Fragment key={u.user_id}>
                    <TableRow
                      className={`cursor-pointer hover:bg-gray-50 ${isExpanded ? 'bg-blue-50/50' : ''}`}
                      onClick={() => hasDetail && toggleUser(u.user_id)}
                      data-testid={`user-row-${u.user_name.replace(/\s+/g,'-')}`}
                    >
                      <TableCell className="w-8 pl-4">
                        {hasDetail && (isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />)}
                      </TableCell>
                      <TableCell className="font-medium">{u.user_name}</TableCell>
                      <TableCell>{u.roles.filter(r => r === 'broker' || r === 'iso').map(r => roleBadge(r))}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{u.broker_deals_count || '—'}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{u.broker_commission_total > 0 ? formatCurrency(u.broker_commission_total) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-green-600">{u.total_paid > 0 ? formatCurrency(u.total_paid) : '—'}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold">
                        {u.total_owed > 0 ? <span className="text-red-600">{formatCurrency(u.total_owed)}</span> : '—'}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-slate-50 p-0">
                          <div className="p-4 space-y-4">
                            {/* Tab toggles: Broker Deals | Payment History */}
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); switchTab(u.user_id, 'broker'); }}
                                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${tab === 'broker' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border'}`}
                                data-testid={`tab-broker-${u.user_id}`}
                              >
                                Broker Deals ({u.broker_deals_count})
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); switchTab(u.user_id, 'history'); }}
                                className={`px-3 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${tab === 'history' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border'}`}
                                data-testid={`tab-history-${u.user_id}`}
                              >
                                <History className="h-3 w-3" /> Payment History
                              </button>
                            </div>

                            {/* ── Broker Deals Tab ── */}
                            {tab === 'broker' && u.broker_deals.length > 0 && (
                              <div>
                                <table className="w-full text-sm">
                                  <thead><tr className="text-left text-gray-500 text-xs">
                                    <th className="pb-2 w-8"></th><th className="pb-2">Deal</th><th className="pb-2">Name</th><th className="pb-2">Funding</th><th className="pb-2 text-right">Commission</th><th className="pb-2">Funded</th><th className="pb-2 text-center">Status</th><th className="pb-2 text-center">Action</th>
                                  </tr></thead>
                                  <tbody>
                                    {u.broker_deals.map((d) => (
                                      <React.Fragment key={d.deal_id}>
                                      <tr
                                        className={`border-t border-gray-200 ${d.paid ? 'opacity-60' : ''} ${d.is_clawed_back ? 'bg-red-50/60' : ''}`}
                                        data-testid={`broker-commission-row-${d.deal_id}`}
                                      >
                                        <td className="py-1.5">
                                          {!d.paid && !d.is_clawed_back && (
                                            <input
                                              type="checkbox"
                                              checked={!!selectedDeals[d.deal_id]}
                                              onChange={() => toggleDealSelection(d.deal_id, d.commission)}
                                              onClick={e => e.stopPropagation()}
                                              className="h-4 w-4 rounded"
                                              data-testid={`pay-check-${d.deal_id}`}
                                            />
                                          )}
                                        </td>
                                        <td className={`py-1.5 font-mono text-xs ${d.is_clawed_back ? 'line-through text-red-700' : ''}`}>#{d.deal_id}</td>
                                        <td className={`py-1.5 ${d.is_clawed_back ? 'line-through text-red-700' : ''}`}>{d.deal_name}</td>
                                        <td className={`py-1.5 font-mono text-xs ${d.is_clawed_back ? 'line-through text-red-700/70' : 'text-gray-700'}`}>
                                          {formatCurrency(d.funding_amount).replace('$','')} x {d.rate || '—'} x {d.term_days ? Math.round(d.term_days) : '—'}{d.fees && d.funding_amount ? ` - ${Math.round((d.fees / d.funding_amount) * 100)}% fees` : ''}
                                        </td>
                                        <td className={`py-1.5 text-right font-mono font-bold ${d.is_clawed_back ? 'line-through text-red-700' : 'text-green-700'}`}>{formatCurrency(d.commission)}</td>
                                        <td className="py-1.5 text-xs text-gray-500">{d.funding_date ? new Date(d.funding_date).toLocaleDateString() : '—'}</td>
                                        <td className="py-1.5 text-center">
                                          {d.is_clawed_back
                                            ? <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid={`clawed-badge-${d.deal_id}`}>Clawed Back</Badge>
                                            : d.paid
                                            ? <Badge className="bg-green-100 text-green-700 text-[10px]">Paid</Badge>
                                            : <Badge className="bg-amber-100 text-amber-700 text-[10px]">Owed</Badge>
                                          }
                                        </td>
                                        <td className="py-1.5 text-center">
                                          {d.is_clawed_back && isAdmin ? (
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                              onClick={(e) => { e.stopPropagation(); openReverseClawbackDialog(d, u.user_id); }}
                                              data-testid={`reverse-clawback-btn-${d.deal_id}`}
                                            >
                                              <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                              Reverse
                                            </Button>
                                          ) : !d.paid && (
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 px-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                                              onClick={(e) => { e.stopPropagation(); openClawbackDialog(d, u.user_id); }}
                                              data-testid={`clawback-btn-${d.deal_id}`}
                                            >
                                              <ShieldAlert className="h-3.5 w-3.5 mr-1" />
                                              Default / Clawback
                                            </Button>
                                          )}
                                        </td>
                                      </tr>
                                      {d.is_clawed_back && (
                                        <tr className="bg-red-50/30 border-t border-red-100" data-testid={`clawback-audit-footer-${d.deal_id}`}>
                                          <td></td>
                                          <td colSpan={7} className="py-1 pb-2 text-[11px] text-red-700/80 italic">
                                            <ShieldAlert className="inline h-3 w-3 mr-1 align-text-bottom" />
                                            Clawed back by{' '}
                                            <span className="font-medium not-italic">{d.clawed_back_by_name || 'unknown user'}</span>
                                            {d.clawed_back_at && (
                                              <>
                                                {' '}on{' '}
                                                <span className="not-italic">{new Date(d.clawed_back_at).toLocaleDateString()}</span>
                                              </>
                                            )}
                                            {' '}·{' '}
                                            <span className="not-italic">{d.clawback_reason ? `reason: ${d.clawback_reason}` : 'no reason recorded'}</span>
                                          </td>
                                        </tr>
                                      )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                                {u.broker_deals.some(d => !d.paid) && (
                                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-sm text-gray-500">
                                      {selectedCount > 0 && (
                                        <span className="font-medium text-gray-800">
                                          {selectedCount} deal{selectedCount !== 1 ? 's' : ''} selected — {formatCurrency(selectedTotal)}
                                        </span>
                                      )}
                                    </div>
                                    <Button
                                      size="sm"
                                      disabled={selectedCount === 0}
                                      onClick={(e) => { e.stopPropagation(); openPayDialog(u.user_id, u.user_name); }}
                                      data-testid={`pay-selected-btn-${u.user_id}`}
                                    >
                                      <DollarSign className="h-3.5 w-3.5 mr-1" />
                                      Pay Selected
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                            {tab === 'broker' && u.broker_deals.length === 0 && (
                              <p className="text-sm text-gray-400">No broker deals</p>
                            )}

                            {/* ── Payment History Tab ── */}
                            {tab === 'history' && (
                              <div>
                                {loadingPayments[u.user_id] ? (
                                  <div className="animate-pulse h-16 bg-gray-100 rounded" />
                                ) : payments.length === 0 ? (
                                  <p className="text-sm text-gray-400 py-4 text-center">No payment history for this user</p>
                                ) : (
                                  <table className="w-full text-sm" data-testid={`payment-history-${u.user_id}`}>
                                    <thead><tr className="text-left text-gray-500 text-xs">
                                      <th className="pb-2">Date</th><th className="pb-2">Method</th><th className="pb-2">Reference</th><th className="pb-2 text-right">Amount</th><th className="pb-2">Deals</th><th className="pb-2">Notes</th><th className="pb-2 text-center">Action</th>
                                    </tr></thead>
                                    <tbody>
                                      {payments.map((p) => (
                                        <tr key={p.id} className="border-t border-gray-200">
                                          <td className="py-1.5 text-xs">{p.payment_date || (p.created_at ? new Date(p.created_at).toLocaleDateString() : '—')}</td>
                                          <td className="py-1.5 capitalize text-xs">{p.payment_method || '—'}</td>
                                          <td className="py-1.5 font-mono text-xs">{p.reference_number || '—'}</td>
                                          <td className="py-1.5 text-right font-mono font-bold text-green-700">{formatCurrency(p.total_amount)}</td>
                                          <td className="py-1.5 text-xs text-gray-500">{(p.deal_payments || []).length} deal{(p.deal_payments || []).length !== 1 ? 's' : ''}</td>
                                          <td className="py-1.5 text-xs text-gray-500 max-w-[120px] truncate">{p.notes || '—'}</td>
                                          <td className="py-1.5 text-center">
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 px-2 text-xs text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                                              disabled={undoing === p.id}
                                              onClick={(e) => { e.stopPropagation(); handleUndoPayment(p.id, u.user_id); }}
                                              data-testid={`undo-payment-btn-${p.id}`}
                                            >
                                              <Undo2 className="h-3.5 w-3.5 mr-1" />
                                              {undoing === p.id ? 'Undoing...' : 'Undo'}
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="pay-commission-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Record Commission Payment
            </DialogTitle>
            <DialogDescription>
              Paying {payUserName} — {selectedCount} deal{selectedCount !== 1 ? 's' : ''} — {formatCurrency(selectedTotal)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payment Method</Label>
                <select
                  value={payForm.payment_method}
                  onChange={e => setPayForm({ ...payForm, payment_method: e.target.value })}
                  className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm"
                  data-testid="pay-method-select"
                >
                  <option value="wire">Wire Transfer</option>
                  <option value="ach">ACH</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Payment Date</Label>
                <Input
                  type="date"
                  value={payForm.payment_date}
                  onChange={e => setPayForm({ ...payForm, payment_date: e.target.value })}
                  className="h-9"
                  data-testid="pay-date-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reference Number</Label>
              <Input
                value={payForm.reference_number}
                onChange={e => setPayForm({ ...payForm, reference_number: e.target.value })}
                placeholder="Wire ref / Check # / ACH trace"
                className="h-9"
                data-testid="pay-ref-input"
              />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={payForm.notes}
                onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
                placeholder="Optional notes..."
                className="h-16 text-sm"
                data-testid="pay-notes-input"
              />
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <div className="text-lg font-bold">{formatCurrency(selectedTotal)}</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPayDialogOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={handlePay} disabled={paying || selectedCount === 0} data-testid="confirm-pay-btn">
                  {paying ? 'Recording...' : 'Confirm Payment'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clawback Confirmation Dialog */}
      <Dialog open={clawbackDialogOpen} onOpenChange={setClawbackDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="clawback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="h-5 w-5" /> Clawback Commission
            </DialogTitle>
            <DialogDescription>
              This action cannot be easily undone.
            </DialogDescription>
          </DialogHeader>
          {clawbackDeal && (
            <div className="space-y-3 pt-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-red-800">Clawback?</p>
                <p className="text-red-700 mt-1">
                  This will remove the commission from the broker's page for deal <span className="font-mono font-bold">#{clawbackDeal.deal_id}</span> — {clawbackDeal.deal_name}.
                </p>
                <p className="text-red-700 mt-1 font-semibold">
                  Commission: {formatCurrency(clawbackDeal.commission)}
                </p>
              </div>
              <div>
                <Label className="text-xs">Reason <span className="text-gray-400">(optional, recommended)</span></Label>
                <Textarea
                  value={clawbackReason}
                  onChange={(e) => setClawbackReason(e.target.value)}
                  placeholder="Why is this commission being clawed back? (e.g. deal defaulted in 30 days, fraud detected)"
                  className="h-16 text-sm"
                  data-testid="clawback-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setClawbackDialogOpen(false)} data-testid="clawback-cancel-btn">Cancel</Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleClawback}
                  disabled={clawingBack}
                  data-testid="clawback-confirm-btn"
                >
                  {clawingBack ? 'Processing...' : 'Confirm Clawback'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reverse Clawback Dialog (Phase 2A) */}
      <Dialog open={reverseDialogOpen} onOpenChange={setReverseDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="reverse-clawback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <RotateCcw className="h-5 w-5" /> Reverse Clawback
            </DialogTitle>
            <DialogDescription>
              Restores the broker commission and voids syndicator COMMISSION_CREDIT rows.
            </DialogDescription>
          </DialogHeader>
          {reverseDeal && (
            <div className="space-y-3 pt-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800">Reverse the clawback?</p>
                <p className="text-blue-700 mt-1">
                  This will mark deal <span className="font-mono font-bold">#{reverseDeal.deal_id}</span> — {reverseDeal.deal_name} — as no longer clawed back and void any associated COMMISSION_CREDIT entries.
                </p>
                <p className="text-blue-700 mt-1 font-semibold">
                  Commission: {formatCurrency(reverseDeal.commission)}
                </p>
              </div>
              <div>
                <Label className="text-xs">Reason <span className="text-red-500">*</span></Label>
                <Textarea
                  value={reverseReason}
                  onChange={(e) => setReverseReason(e.target.value)}
                  placeholder="Why is this clawback being reversed?"
                  className="h-20 text-sm"
                  data-testid="reverse-clawback-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setReverseDialogOpen(false)} data-testid="reverse-clawback-cancel-btn">Cancel</Button>
                <Button
                  size="sm"
                  onClick={handleReverseClawback}
                  disabled={reversing || !reverseReason.trim()}
                  data-testid="reverse-clawback-confirm-btn"
                >
                  {reversing ? 'Processing...' : 'Confirm Reverse'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Commissions = ({ embedded = false }) => {
  // 11/03 (Josh: consolidate pages): `embedded` renders the body without the app shell, inside a hub tab.
  const Wrap = embedded ? React.Fragment : Layout;
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [myDeals, setMyDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestNotes, setRequestNotes] = useState('');
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [referenceCode, setReferenceCode] = useState('');
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);

  const isAdmin = user?.roles?.includes('admin');
  const isBrokerISO = user?.roles?.includes('broker') || user?.roles?.includes('iso');

  const canRequestCommission = (deal) => {
    if (!deal.funding_date || !deal.commission_rate || !deal.commission_type) return false;
    
    const fundingDate = new Date(deal.funding_date);
    const twentyDaysLater = new Date(fundingDate);
    twentyDaysLater.setDate(twentyDaysLater.getDate() + 20);
    
    return new Date() >= twentyDaysLater;
  };

  const fetchData = useCallback(async () => {
    try {
      if (isBrokerISO) {
        const [balanceRes, dealsRes, requestsRes] = await Promise.all([
          axios.get(`${API}/commissions/my-balance`, { headers: getAuthHeaders() }),
          axios.get(`${API}/deals`, { headers: getAuthHeaders() }),
          axios.get(`${API}/commissions/requests`, { headers: getAuthHeaders() })
        ]);
        setBalance(balanceRes.data);
        
        const allDeals = dealsRes.data || [];
        const allRequests = requestsRes.data || [];

        const requestedDealIds = new Set(allRequests.map(req => req.deal_id));
        const paidDealIds = new Set(
          allRequests
            .filter(req => req.status === 'Paid')
            .map(req => req.deal_id)
        );
        
        const availableDeals = allDeals
          .filter(deal => canRequestCommission(deal))
          .map(deal => ({
            ...deal,
            hasRequest: requestedDealIds.has(deal.id),
            isPaid: paidDealIds.has(deal.id),
          }));
        
        setMyDeals(availableDeals);
        setRequests(allRequests);
      } else if (isAdmin) {
        const requestsRes = await axios.get(`${API}/commissions/requests`, { headers: getAuthHeaders() });
        setRequests(requestsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch commission data:', error);
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isBrokerISO, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculateCommission = (deal) => {
    if (!deal.commission_rate || !deal.commission_type) return 0;
    
    if (deal.commission_type === 'iso') {
      return deal.funding_amount * (deal.commission_rate / 100);
    } else {
      const points = (deal.rate - 1) * deal.funding_amount;
      return points * (deal.commission_rate / 100);
    }
  };

  const handleRequestCommission = async () => {
    if (selectedDeals.length === 0) {
      toast.error('Please select at least one deal');
      return;
    }

    try {
      const totalAmount = selectedDeals.reduce((sum, dealId) => {
        const deal = myDeals.find(d => d.id === dealId);
        return sum + calculateCommission(deal);
      }, 0);

      await Promise.all(
        selectedDeals.map(dealId => {
          const deal = myDeals.find(d => d.id === dealId);
          return axios.post(
            `${API}/commissions/request`,
            null,
            {
              headers: getAuthHeaders(),
              params: {
                deal_id: dealId,
                amount: calculateCommission(deal),
                notes: requestNotes || undefined
              }
            }
          );
        })
      );

      toast.success(`Commission request submitted for ${formatCurrency(totalAmount)}`);
      setRequestDialogOpen(false);
      setSelectedDeals([]);
      setRequestNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request commission');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await axios.put(`${API}/commissions/${requestId}/approve`, null, { headers: getAuthHeaders() });
      toast.success('Commission request approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (requestId, reason) => {
    try {
      await axios.put(
        `${API}/commissions/${requestId}/reject`,
        null,
        {
          headers: getAuthHeaders(),
          params: { reason }
        }
      );
      toast.success('Commission request rejected');
      setAdminDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedRequest) return;

    try {
      await axios.put(
        `${API}/commissions/${selectedRequest.id}/mark-paid`,
        { reference_code: referenceCode, paid_date: paidDate },
        { headers: getAuthHeaders() }
      );
      toast.success('Commission marked as paid');
      setAdminDialogOpen(false);
      setReferenceCode('');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Paid': 'bg-blue-100 text-blue-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="px-4 sm:px-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{isAdmin ? 'ISO Commissions' : 'Commission Management'}</h1>
          <p className="text-gray-600">{isAdmin ? 'What is scheduled, paid and still owed to each ISO, by deal' : 'Track and request your commission payments'}</p>
        </div>

        {/* Broker/ISO View */}
        {isBrokerISO && balance && (
          <>
            {/* Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Earned</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    {formatCurrency(balance.total_earned)}
                  </div>
                  <p className="text-xs text-gray-500">All time commissions</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Requested</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">
                    {formatCurrency(balance.total_requested)}
                  </div>
                  <p className="text-xs text-gray-500">Pending approval</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Paid</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(balance.total_paid)}
                  </div>
                  <p className="text-xs text-gray-500">Received payments</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Available Balance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">
                    {formatCurrency(balance.available_balance)}
                  </div>
                  <p className="text-xs text-gray-500">Ready to request</p>
                </CardContent>
              </Card>
            </div>

            {/* Available Deals */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Available for Commission Request</CardTitle>
                  <Button 
                    onClick={() => setRequestDialogOpen(true)}
                    disabled={selectedDeals.length === 0}
                  >
                    Request Commission ({selectedDeals.length})
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedDeals.length === myDeals.length && myDeals.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedDeals(myDeals.map(d => d.id));
                            } else {
                              setSelectedDeals([]);
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Deal ID</TableHead>
                      <TableHead>Merchant</TableHead>
                      <TableHead>Funded Amount</TableHead>
                      <TableHead>Commission Type</TableHead>
                      <TableHead>Commission Rate</TableHead>
                      <TableHead>Your Commission</TableHead>
                      <TableHead>Funding Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myDeals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                          No deals available for commission request yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      myDeals.map(deal => {
                        const isDisabled = deal.isPaid || deal.hasRequest;
                        return (
                          <TableRow
                            key={deal.id}
                            className={isDisabled ? 'opacity-50 bg-gray-50' : ''}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedDeals.includes(deal.id)}
                                disabled={isDisabled}
                                onCheckedChange={(checked) => {
                                  if (checked && !isDisabled) {
                                    setSelectedDeals([...selectedDeals, deal.id]);
                                  } else {
                                    setSelectedDeals(selectedDeals.filter(id => id !== deal.id));
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{deal.deal_id}</TableCell>
                            <TableCell>
                              {deal.deal_name}
                              {deal.isPaid && (
                                <Badge className="ml-2 bg-green-100 text-green-800">Paid</Badge>
                              )}
                              {!deal.isPaid && deal.hasRequest && (
                                <Badge className="ml-2 bg-yellow-100 text-yellow-800">Requested</Badge>
                              )}
                            </TableCell>
                            <TableCell>{formatCurrency(deal.funding_amount)}</TableCell>
                            <TableCell className="capitalize">{deal.commission_type}</TableCell>
                            <TableCell>{deal.commission_rate}%</TableCell>
                            <TableCell className="font-bold text-green-600">
                              {formatCurrency(calculateCommission(deal))}
                            </TableCell>
                            <TableCell>{new Date(deal.funding_date).toLocaleDateString()}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Request History */}
            <Card>
              <CardHeader>
                <CardTitle>My Commission Requests</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request Date</TableHead>
                      <TableHead>Deal</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-10 text-gray-500">No commission requests yet.</TableCell></TableRow>
                    ) : requests.map(req => (
                      <TableRow key={req.id}>
                        <TableCell>{new Date(req.request_date).toLocaleDateString()}</TableCell>
                        <TableCell>{req.deal_name}</TableCell>
                        <TableCell className="font-bold">{formatCurrency(req.commission_amount)}</TableCell>
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                        <TableCell>{req.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Request Dialog */}
            <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Commission Payment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      You are requesting commission for {selectedDeals.length} deal(s)
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      Total: {formatCurrency(selectedDeals.reduce((sum, id) => {
                        const deal = myDeals.find(d => d.id === id);
                        return sum + calculateCommission(deal);
                      }, 0))}
                    </p>
                  </div>
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={requestNotes}
                      onChange={(e) => setRequestNotes(e.target.value)}
                      placeholder="Add any notes for this request..."
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setRequestDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleRequestCommission}>
                      Submit Request
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Admin View */}
        {isAdmin && (
          <>
            <IsoOutstanding getAuthHeaders={getAuthHeaders} />
            <SystemBreakdown getAuthHeaders={getAuthHeaders} />

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>All Commission Requests</CardTitle>
              </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Deal</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-10 text-gray-500">No commission requests yet. Pending and approved requests will appear here.</TableCell></TableRow>
                  ) : requests.map(req => (
                    <TableRow key={req.id}>
                      <TableCell>{new Date(req.request_date).toLocaleDateString()}</TableCell>
                      <TableCell>{req.user_name}</TableCell>
                      <TableCell>{req.deal_name}</TableCell>
                      <TableCell className="capitalize">{req.commission_type}</TableCell>
                      <TableCell className="font-bold">{formatCurrency(req.commission_amount)}</TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          {req.status === 'Pending' && (
                            <>
                              <Button size="sm" onClick={() => handleApprove(req.id)}>
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => {
                                  const reason = prompt('Rejection reason:');
                                  if (reason) handleReject(req.id, reason);
                                }}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {req.status === 'Approved' && (
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSelectedRequest(req);
                                setAdminDialogOpen(true);
                              }}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            </Card>
          </>
        )}

        {/* Admin Mark Paid Dialog */}
        <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark Commission as Paid</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">
                  Marking commission for {selectedRequest?.user_name}
                </p>
                <p className="text-xl font-bold text-green-600">
                  Amount: {formatCurrency(selectedRequest?.commission_amount)}
                </p>
              </div>
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Reference Code (Internal Only)</Label>
                <Input
                  value={referenceCode}
                  onChange={(e) => setReferenceCode(e.target.value)}
                  placeholder="Check #, ACH ref, etc."
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setAdminDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleMarkPaid}>
                  Confirm Payment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Wrap>
  );
};

export default Commissions;
